import {GlobalEventBus} from "../GlobalEventBus";
import {WIDGET_STATE_EVENTS} from "../../Events/Widget/State";
import {BaseWidgetConfiguration} from "../../Contracts/WidgetTypes/BaseWidget";
import {useWidgetNavigation} from "../../Composables/WidgetNavigation/useWidgetNavigation";
import {SESSION_EVENTS} from "../../Events/Session";
import {CHATBOT_EVENTS} from "../../Events/ChatBot/Events";
import {CheckoutRoute} from "../../Composables/WidgetNavigation/CheckoutRoute";
import {canShowCheckout} from "./canShowCheckout";
import {CheckoutInitiator} from "../../ValueObjects/CheckoutInitiator";
import {CheckoutSuccessRoute} from "../../Composables/WidgetNavigation/CheckoutSuccessRoute";

interface MountedWidget {
    widget: BaseWidgetConfiguration;
    widgetNavigation: ReturnType<typeof useWidgetNavigation>;
    hostElement?: HTMLElement | null;
}

export class WidgetsOrchestratorService {
    private widgets: MountedWidget[] = [];
    private eventBus: typeof GlobalEventBus;
    private successToken: string | null = null;
    private widgetShowingSuccess: string | null = null;
    private activeChatbotWidgetId: string | undefined;

    constructor(eventBus: typeof GlobalEventBus) {
        this.eventBus = eventBus;
        this.listenToWidgetEvents();
        this.listenToSessionEvents();
        this.listenToChatBotEvents();
        this.registerCheckoutLinkInterceptor();
    }

    /**
     * Picks the widget that should host the checkout: the one whose chat the
     * user is talking to, falling back to the first mounted widget. Instances
     * that cannot show it are out of the running, so a hidden copy of a widget
     * never swallows the checkout.
     * Returns undefined when no mounted widget can render the checkout.
     */
    private resolveCheckoutHost = (preferWidgetId?: string): MountedWidget | undefined => {
        const candidates = this.widgets.filter(w => canShowCheckout(w.hostElement));
        const findById = (id?: string) =>
            id ? candidates.find(w => w.widget.id === id) : undefined;

        return findById(preferWidgetId)
            ?? findById(this.activeChatbotWidgetId)
            ?? candidates[0];
    }

    private openCheckout = (url: string, initiator: CheckoutInitiator, preferWidgetId?: string): void => {
        const target = this.resolveCheckoutHost(preferWidgetId);
        if (!target) return;

        target.widgetNavigation.navigateTo(new CheckoutRoute({
            directCheckoutUrl: url,
            initiator,
        }));
    }

    private listenToChatBotEvents = () => {
        this.eventBus.listen(CHATBOT_EVENTS.CHATBOT_OPENED, ({id}) => {
            this.activeChatbotWidgetId = id;
        });

        // An id-less close event is a broadcast; an id-carrying one only
        // clears the widget it names, so another chat closing is ignored.
        this.eventBus.listen(CHATBOT_EVENTS.CHATBOT_CLOSED, ({id}) => {
            if (id && id !== this.activeChatbotWidgetId) return;
            this.activeChatbotWidgetId = undefined;
        });
    }

    private listenToWidgetEvents = () => {
        this.eventBus.listen(WIDGET_STATE_EVENTS.WIDGET_MOUNTED, (payload) => {
            this.widgets.push(payload);
            this.widgetShowingSuccess ??= payload.widget.id;

            if (this.successToken && payload.widget.id === this.widgetShowingSuccess && !window.BookingKitApp) {
                payload.widgetNavigation.navigateTo(new CheckoutSuccessRoute({successPageToken: this.successToken}));
            }
        });

        this.eventBus.listen(WIDGET_STATE_EVENTS.WIDGET_UNMOUNTED, ({widget, hostElement}) => {
            // Several instances can share one widget id, so drop the one that
            // actually went away rather than the first with a matching id
            const index = hostElement
                ? this.widgets.findIndex(w => w.hostElement === hostElement)
                : this.widgets.findIndex(w => w.widget.id === widget.id);
            if (index !== -1) this.widgets.splice(index, 1);
        });

        this.eventBus.listen(WIDGET_STATE_EVENTS.WIDGET_OPEN_CHECKOUT, ({url, preferWidgetId}) => {
            // preferWidgetId comes from outside the widget, so it can be an invalid initiator id
            const initiatorId = preferWidgetId && CheckoutInitiator.isValidId(preferWidgetId) ? preferWidgetId : 'externalevent';
            const checkoutInitiator = CheckoutInitiator.unknownLocation(initiatorId);
            this.openCheckout(url, checkoutInitiator, preferWidgetId);
        });
    }

    /**
     * Intercepts clicks on checkout links (utm_source=web_chat) from any
     * chat provider on the page — works even if punku is not configured
     * on the widget itself.
     */
    private registerCheckoutLinkInterceptor = () => {
        document.addEventListener('click', (e) => {
            const anchor = e.composedPath().find(
                (el) => el instanceof HTMLAnchorElement && el.href
            ) as HTMLAnchorElement | undefined;
            if (!anchor) return;

            const url = new URL(anchor.href, location.href);
            if (url.searchParams.get('utm_source') !== 'web_chat') return;

            const checkoutOrigin = new URL(import.meta.env.VITE_BK_CHECKOUT_BASE_URL).origin;
            if (url.origin !== checkoutOrigin) return;

            if (
                e.defaultPrevented ||
                e.button !== 0 ||
                e.metaKey ||
                e.ctrlKey ||
                e.shiftKey ||
                e.altKey
            ) return;

            // Never swallow the click unless a widget is there to show the checkout —
            // a new tab is a far better outcome than a link that does nothing.
            const host = this.resolveCheckoutHost();
            if (!host) return;

            e.preventDefault();
            host.widgetNavigation.navigateTo(new CheckoutRoute({
                directCheckoutUrl: url.href,
                initiator: CheckoutInitiator.unknownLocation('webchatlinkclick'),
            }));
        }, true);
    }

    private listenToSessionEvents = () => {
        this.eventBus.listen(SESSION_EVENTS.CHECKOUT_SUCCESSFUL, ({token}) => {
            this.successToken = token;
        });

        this.eventBus.listen(SESSION_EVENTS.CHECKOUT_SUCCESSFUL_SHOWN, () => {
            this.successToken = null;
            this.widgetShowingSuccess = null;
        });
    }
}
